import React from 'react';
import axios from 'axios';
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Grid,
  Stack,
} from '@mui/material';
import { LocalizedAlert as Alert, LocalizedButton as Button, LocalizedTextField as TextField } from '../components/i18n/LocalizedMui';
import Typography from '../components/i18n/LocalizedTypography';
import { WbSunny } from '@mui/icons-material';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import CitySearch from '../components/forms/CitySearch';
import PanchangAlmanac from '../components/panchang/PanchangAlmanac';
import DailyAstrologyShareCard from '../components/DailyAstrologyShareCard';
import { formatDisplayDate as formatPreferredDate } from '../utils/dateDisplay';

const API_BASE_URL = process.env.REACT_APP_STUDIO_API_URL || 'http://localhost:4173/api/studio/v1';

interface City {
  id: string;
  name: string;
  displayName: string;
  coordinates: {
    latitude: number;
    longitude: number;
  };
  timezone: {
    name: string;
    offset: number;
  };
  population: number;
  admin1?: {
    name: string;
    code?: string;
  };
  country?: {
    name: string;
    code: string;
  };
}

interface PanchangResult {
  date: string;
  day: string;
  tithi: string;
  nakshatra: string;
  yoga: string;
  karana: string;
  sunrise: string;
  sunset: string;
  details: {
    tithiNumber: number;
    nakshatraNumber: number;
    nakshatraPada: number;
    yogaNumber: number;
    karanaNumber: number;
  };
  [key: string]: any;
}

const formatDateForInput = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatDisplayDate = (value: string, isHindi: boolean) => (
  formatPreferredDate(value)
);

const Panchang: React.FC = () => {
  const { i18n } = useTranslation();
  const isHindi = Boolean(i18n.resolvedLanguage?.startsWith('hi') || i18n.language?.startsWith('hi'));
  const [selectedCity, setSelectedCity] = React.useState<City | null>(null);
  const [selectedDate, setSelectedDate] = React.useState(formatDateForInput(new Date()));
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [result, setResult] = React.useState<PanchangResult | null>(null);

  React.useEffect(() => {
    document.title = 'Panchang - Kundlistar';
  }, []);

  const calculate = async () => {
    if (!selectedCity) {
      setError('Please select a city first.');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(`${API_BASE_URL}/panchang`, {
        params: {
          lat: selectedCity.coordinates.latitude,
          lon: selectedCity.coordinates.longitude,
          timezoneOffset: selectedCity.timezone.offset,
          date: selectedDate
        }
      });
      setResult(response.data.data.panchang);
    } catch (err: any) {
      setError(err.response?.data?.detail || err.response?.data?.error || 'Failed to calculate Panchang.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Panchang Calculator | Tithi, Nakshatra, Yoga, Karana | Kundlistar</title>
        <meta
          name="description"
          content="Calculate Panchang for any date and location with Vara, Tithi, Nakshatra, Yoga, Karana, sunrise and sunset."
        />
      </Helmet>

      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
            Panchang
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Daily Hindu almanac with Vara, Tithi, Nakshatra, Yoga, Karana, sunrise and sunset.
          </Typography>
        </Box>

        <Card sx={{ borderRadius: 3, mb: 4 }}>
          <CardContent>
            <Grid container spacing={2} alignItems="stretch">
              <Grid size={{ xs: 12, md: 7 }}>
                <CitySearch
                  value={selectedCity}
                  onChange={(city) => {
                    setSelectedCity(city);
                    setError(null);
                  }}
                  label="City"
                  placeholder="Search city..."
                  required
                />
              </Grid>
              <Grid size={{ xs: 12, md: 3 }}>
                <TextField
                  fullWidth
                  type="date"
                  label="Date"
                  value={selectedDate}
                  onChange={(event) => setSelectedDate(event.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 2 }}>
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <WbSunny />}
                  onClick={calculate}
                  disabled={loading || !selectedCity}
                  sx={{ minHeight: 56, height: '100%', fontWeight: 600 }}
                >
                  {loading ? 'Calculating...' : 'Calculate'}
                </Button>
              </Grid>
            </Grid>

            {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
          </CardContent>
        </Card>

        {result && (
          <Stack spacing={3}>
            <DailyAstrologyShareCard
              title={isHindi ? 'आज का पंचांग' : "Today's Panchang"}
              subtitle={isHindi ? 'कुंडलिस्टार दैनिक पंचांग' : 'Daily Panchang by Kundlistar'}
              date={formatDisplayDate(result.date || selectedDate, isHindi)}
              location={selectedCity?.displayName || selectedCity?.name || 'Selected city'}
              rows={[
                { label: isHindi ? 'तिथि' : 'Tithi', value: result.tithi },
                { label: isHindi ? 'नक्षत्र' : 'Nakshatra', value: result.nakshatra },
                { label: isHindi ? 'योग' : 'Yoga', value: result.yoga },
                { label: isHindi ? 'करण' : 'Karana', value: result.karana },
                { label: isHindi ? 'सूर्योदय' : 'Sunrise', value: result.sunrise },
                { label: isHindi ? 'सूर्यास्त' : 'Sunset', value: result.sunset },
                { label: isHindi ? 'चंद्र राशि' : 'Moon Sign', value: result.rashi?.moonSign },
              ]}
              textMessage={[
                `${isHindi ? 'आज का पंचांग' : "Today's Panchang"} - ${formatDisplayDate(result.date || selectedDate, isHindi)}`,
                selectedCity?.displayName || selectedCity?.name || 'Selected city',
                '',
                `${isHindi ? 'तिथि' : 'Tithi'}: ${result.tithi || '—'}`,
                `${isHindi ? 'नक्षत्र' : 'Nakshatra'}: ${result.nakshatra || '—'}`,
                `${isHindi ? 'योग' : 'Yoga'}: ${result.yoga || '—'}`,
                `${isHindi ? 'करण' : 'Karana'}: ${result.karana || '—'}`,
                `${isHindi ? 'सूर्योदय' : 'Sunrise'}: ${result.sunrise || '—'}`,
                `${isHindi ? 'सूर्यास्त' : 'Sunset'}: ${result.sunset || '—'}`,
                '',
                isHindi ? 'कुंडलिस्टार से साझा किया गया' : 'Shared via Kundlistar',
                'Download App: https://kundlistarstudio.com?utm_source=share&utm_medium=website&utm_campaign=daily_panchang'
              ].join('\n')}
              imageFileName="kundlistar-daily-panchang.png"
              shareTitle={isHindi ? 'कुंडलिस्टार पंचांग' : 'Kundlistar Panchang'}
              imageButtonLabel={isHindi ? 'इमेज शेयर करें' : 'Share Image'}
              textButtonLabel={isHindi ? 'टेक्स्ट शेयर करें' : 'Share Text'}
              copyButtonLabel={isHindi ? 'टेक्स्ट कॉपी करें' : 'Copy Text'}
            />
            <PanchangAlmanac
              panchang={result}
              locationLabel={selectedCity?.displayName || selectedCity?.name}
            />
          </Stack>
        )}
      </Container>
    </>
  );
};

export default Panchang;
